// @dynamic-i18n-keys
// Dynamic t() at line 131 (isMobile suffix):
//   deviationSubmit.form.galleries.label
//   deviationSubmit.form.galleries.label.mobile
import React, { useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { pushConfirmationModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import GalleryMultiselect, { GallerySelectItem } from '../../common/GalleryMultiselect';
import { useSubmitField } from '../../hooks/useSubmitField';
import { deselectItems, anySelected } from '../../utils/select';
import { isWatchersOnly } from '../../utils/galleries';
import { SubmitFormContext } from '../../context';
import { initAdapter, submitAdapter } from './adapters';
import { initAdapter as presetsInitAdapter } from './presetAdapters';

import s from './GallerySelectField.scss';

const GallerySelectField: React.FC<{}> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const {
    params: { isPresetsForm },
  } = useContext(SubmitFormContext)!;
  const { id, error, field, ref } = useSubmitField('galleries', {
    initAdapter: isPresetsForm ? presetsInitAdapter : initAdapter,
    submitAdapter,
    derivedFromFields: ['premiumGalleries'],
  });
  const { value = [], onChange } = field;
  const { getValues, setValue, watch, trigger } = useFormContext();

  const confirmRemovePaidAccess = useCallback(
    (onConfirm: () => void, onCancel?: () => void) => {
      dispatch(
        pushConfirmationModal({
          variant: 'horizontal',
          title: t('deviationSubmit.form.confirmViewingPermission.title'),
          text: t('deviationSubmit.form.confirmViewingPermission.text'),
          confirmBtnLabel: t('deviationSubmit.form.confirmViewingPermission.confirmBtn'),
          onConfirm,
          onCancel,
        })
      );
    },
    [dispatch, t]
  );

  const shouldChange = useCallback(
    (newValue: GallerySelectItem[]) => {
      const values = getValues();
      const inGalleries = anySelected(newValue);
      const inWatchersOnly = anySelected(newValue, ({ gallery }) => isWatchersOnly(gallery));
      const inPremiumGalleries = anySelected(values['premiumGalleries']);
      const inTiers = anySelected(values['tiers']);

      if ((inGalleries && inPremiumGalleries) || (inWatchersOnly && inTiers)) {
        return new Promise<boolean>(resolve => {
          confirmRemovePaidAccess(
            () => resolve(true), // onConfirm
            () => resolve(false) // onCancel
          );
        });
      } else {
        return true;
      }
    },
    [getValues, confirmRemovePaidAccess]
  );

  // When any gallery is selected:
  //   - Clear 'premiumGalleries' (paid access cannot coexist with galleries)
  //   - If any selected gallery is "watchers only", clear 'tiers' (tiers cannot coexist with watchers-only)
  //   - Set scraps toggle off
  const handleChange = useCallback(
    (newValue: GallerySelectItem[]) => {
      const inGalleries = anySelected(newValue);
      if (inGalleries) {
        const values = getValues();
        const inPremiumGalleries = anySelected(values['premiumGalleries']);
        const inTiers = anySelected(values['tiers']);
        const inWatchersOnly = anySelected(newValue, ({ gallery }) => isWatchersOnly(gallery));

        if (inPremiumGalleries) {
          setValue('premiumGalleries', deselectItems(values['premiumGalleries']), {
            shouldDirty: true,
          });
        }
        if (inWatchersOnly && inTiers) {
          setValue('tiers', deselectItems(values['tiers']), {
            shouldDirty: true,
          });
        }
        setValue('isScrap', false, { shouldDirty: true });
      }

      onChange(newValue);
    },
    [onChange, setValue, getValues]
  );

  // This field and the scraps toggle work together.
  // When a gallery is selected, the scraps toggle is turned off and viceversa.
  const isScrap = watch('isScrap');
  const clearSelectedGalleries = useCallback(
    () => onChange(deselectItems(value)),
    [onChange, value]
  );

  useEffect(() => {
    if (isScrap) {
      clearSelectedGalleries();
    }
    trigger(field.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrap]);

  return (
    <GalleryMultiselect
      className={s['root']}
      id={id}
      label={t(`deviationSubmit.form.galleries.label${isMobile ? '.mobile' : ''}`)}
      placeholder={t('common.select')}
      error={error?.message}
      items={value}
      shouldChange={shouldChange}
      onChange={handleChange}
      innerRef={el => {
        ref.current = el;
      }}
    />
  );
};
GallerySelectField.displayName = 'GallerySelectField';

export default GallerySelectField;
